import {CopyToClipboardButton} from '@github-ui/copy-to-clipboard'
import {
  actionsWorkflowFilePath,
  actionsWorkflowRunPath,
  branchPath,
  commitHovercardPath,
  repoAttestationDownloadPath,
  repoAttestationsIndexPath,
} from '@github-ui/paths'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {ArrowLeftIcon, DownloadIcon, VerifiedIcon} from '@primer/octicons-react'
import {Box, Button, Heading, Link, PageLayout, RelativeTime, Text, Truncate} from '@primer/react'

import {AttestationPropertiesList} from '../components/AttestationPropertiesList'
import {StaticFileFrame} from '../components/StaticFileFrame'
import {StaticSnippet} from '../components/StaticSnippet'
import type {AttestationPayload} from '../types'

export const AttestationDetail = () => {
  const payload = useRoutePayload<AttestationPayload>()
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, runId, attempt] =
    payload.attestation.runInvocationUri.match(/\/actions\/runs\/(\d+)\/attempts\/(\d+)$/) || []

  const lines: Array<{number: number; html: SafeHTMLString}> = payload.rawLines.map((line, index) => {
    return {
      number: index,
      html: line as SafeHTMLString,
    }
  })

  return (
    <PageLayout>
      <PageLayout.Header>
        <Box>
          <Link
            sx={{color: 'fg.muted'}}
            href={repoAttestationsIndexPath({
              repo: payload.repo,
            })}
          >
            <ArrowLeftIcon size={16} /> Attestations
          </Link>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: ['column', 'column', 'row'],
          }}
        >
          <Heading as="h2" sx={{display: 'flex', flexGrow: 1, alignItems: 'center', fontSize: 4, fontWeight: 'normal'}}>
            <Box sx={{color: 'fg.muted', mr: 2}}>
              <VerifiedIcon size={24} />
            </Box>
            {`${payload.attestation.subjectName} #${payload.attestation.id || 'Untitled Subject'}`}
          </Heading>
          <Box sx={{display: 'flex', alignItems: 'center'}}>
            <Link
              sx={{mr: 3}}
              data-testid="view-build-summary"
              href={actionsWorkflowRunPath({
                repo: payload.repo.name,
                owner: payload.repo.ownerLogin,
                runId,
                attempt,
              })}
            >
              View build summary
            </Link>
            <Button
              as="a"
              data-turbo="false"
              data-testid="download-attestation-detail"
              href={repoAttestationDownloadPath({
                repo: payload.repo,
                attestationId: payload.attestation.id,
              })}
            >
              <DownloadIcon size={16} />
              <Text sx={{pl: 1}}>Download</Text>
            </Button>
          </Box>
        </Box>
      </PageLayout.Header>
      <PageLayout.Content>
        <AttestationPropertiesList
          definitions={[
            {
              propertyName: 'Created',
              propertyValue: (
                <Box sx={{color: 'fg.muted', py: 2, fontSize: 1}}>
                  <RelativeTime
                    data-testid="attestation-relative-time"
                    tense="past"
                    datetime={payload.attestation.createdAt}
                  >
                    {new Date(Date.parse(payload.attestation.createdAt)).toLocaleDateString('default', {
                      month: 'short',
                      day: 'numeric',
                    })}
                  </RelativeTime>{' '}
                  ({new Date(Date.parse(payload.attestation.createdAt)).toUTCString()})
                </Box>
              ),
            },
            {
              propertyName: 'Commit',
              propertyValue: (
                <Truncate
                  title={payload.attestation.sourceRepositoryDigest}
                  sx={{maxWidth: 400, fontFamily: 'monospace', fontSize: 0, color: 'fg.default'}}
                  inline={true}
                >
                  <Link
                    sx={{color: 'fg.default'}}
                    data-testid="attestation-commit-link"
                    href={branchPath({
                      owner: payload.repo.ownerLogin,
                      repo: payload.repo.name,
                      branch: payload.attestation.sourceRepositoryDigest,
                    })}
                    data-hovercard-url={commitHovercardPath({
                      owner: payload.repo.ownerLogin,
                      repo: payload.repo.name,
                      commitish: payload.attestation.sourceRepositoryDigest,
                    })}
                    underline
                  >
                    {payload.attestation.sourceRepositoryDigest}
                  </Link>
                </Truncate>
              ),
            },
            {
              propertyName: 'Subject Digest',
              propertyValue: (
                <>
                  <Truncate
                    title={payload.attestation.subjectDigest}
                    sx={{maxWidth: 310, fontFamily: 'monospace', fontSize: 0, color: 'fg.default'}}
                    inline={true}
                  >
                    {payload.attestation.subjectDigest}
                  </Truncate>
                  <CopyToClipboardButton
                    className="d-flex flex-items-center position-relative"
                    tooltipProps={{direction: 'nw'}}
                    accessibleButton
                    sx={{
                      height: 28,
                      width: 28,
                      right: '-5px',
                    }}
                    textToCopy={payload.attestation.subjectDigest}
                    ariaLabel={'Copy subject digest to clipboard'}
                  />
                </>
              ),
            },
            {
              propertyName: 'Predicate Type',
              propertyValue: (
                <Truncate
                  title={payload.attestation.predicateType}
                  sx={{maxWidth: 400, fontFamily: 'monospace', fontSize: 0, color: 'fg.default'}}
                  inline={true}
                >
                  {payload.attestation.predicateType}
                </Truncate>
              ),
            },
            {
              propertyName: 'Workflow',
              propertyValue: (
                <Truncate
                  title={`${payload.attestation.buildConfigDisplayName}@${payload.attestation.sourceRepositoryRef}`}
                  sx={{maxWidth: 500, fontFamily: 'monospace', fontSize: 0, color: 'fg.default'}}
                  inline={true}
                >
                  <Link
                    sx={{color: 'fg.default'}}
                    data-testid="attestation-workflow-link"
                    href={actionsWorkflowFilePath({
                      repo: payload.repo.name,
                      owner: payload.repo.ownerLogin,
                      runId,
                    })}
                    underline
                  >
                    {payload.attestation.buildConfigDisplayName}@{payload.attestation.sourceRepositoryRef}
                  </Link>
                </Truncate>
              ),
            },
            {
              propertyName: 'Verify',
              propertyValue: (
                <Box sx={{fontSize: 0, py: 2, color: 'fg.default'}}>
                  <code>
                    gh attestation verify &lt;filename-or-url&gt; --owner {payload.repo.ownerLogin} --bundle{' '}
                    <Link
                      href={repoAttestationDownloadPath({
                        repo: payload.repo,
                        attestationId: payload.attestation.id,
                      })}
                      inline
                      sx={{color: 'fg.default'}}
                    >
                      ./
                      {payload.repo.ownerLogin}-{payload.repo.name}-attestation-{payload.attestation.id}.sigstore.json
                    </Link>
                  </code>
                </Box>
              ),
            },
          ]}
        />
        <Heading as="h3" sx={{display: 'flex', flexGrow: 1, alignItems: 'center', fontSize: 4, fontWeight: 'normal'}}>
          Certificate Summary
        </Heading>
        <AttestationPropertiesList
          definitions={[
            {
              propertyName: 'Build Config Digest',
              propertyValue: (
                <Truncate
                  title={payload.attestation.buildConfigDigest}
                  sx={{maxWidth: 400, fontFamily: 'monospace', fontSize: 0, color: 'fg.default'}}
                  inline={true}
                >
                  {payload.attestation.buildConfigDigest}
                </Truncate>
              ),
            },
            {
              propertyName: 'Build Config URI',
              propertyValue: (
                <Truncate
                  title={payload.attestation.buildConfigUri}
                  sx={{maxWidth: 800, fontFamily: 'monospace', fontSize: 0, color: 'fg.default'}}
                  inline={true}
                >
                  {payload.attestation.buildConfigUri}
                </Truncate>
              ),
            },
            {
              propertyName: 'Build Signer Digest',
              propertyValue: (
                <Truncate
                  title={payload.attestation.buildSignerDigest}
                  sx={{maxWidth: 400, fontFamily: 'monospace', fontSize: 0, color: 'fg.default'}}
                  inline={true}
                >
                  {payload.attestation.buildSignerDigest}
                </Truncate>
              ),
            },
            {
              propertyName: 'Build Signer URI',
              propertyValue: (
                <>
                  <Truncate
                    title={payload.attestation.buildSignerUri}
                    sx={{maxWidth: 800, fontFamily: 'monospace', fontSize: 0, color: 'fg.default'}}
                    inline={true}
                  >
                    {payload.attestation.buildSignerUri}
                  </Truncate>
                </>
              ),
            },
            {
              propertyName: 'Build Trigger',
              propertyValue: (
                <Truncate
                  title={payload.attestation.buildTrigger}
                  sx={{maxWidth: 400, fontFamily: 'monospace', fontSize: 0, color: 'fg.default'}}
                  inline={true}
                >
                  {payload.attestation.buildTrigger}
                </Truncate>
              ),
            },
            {
              propertyName: 'Issuer',
              propertyValue: (
                <Truncate
                  title={payload.attestation.issuer}
                  sx={{maxWidth: 400, fontFamily: 'monospace', fontSize: 0, color: 'fg.default'}}
                  inline={true}
                >
                  {payload.attestation.issuer}
                </Truncate>
              ),
            },
            {
              propertyName: 'Runner Invocation URI',
              propertyValue: (
                <Truncate
                  title={payload.attestation.runInvocationUri}
                  sx={{maxWidth: 800, fontFamily: 'monospace', fontSize: 0, color: 'fg.default'}}
                  inline={true}
                >
                  {payload.attestation.runInvocationUri}
                </Truncate>
              ),
            },
            {
              propertyName: 'Runner Environment',
              propertyValue: (
                <Truncate
                  title={payload.attestation.runnerEnvironment}
                  sx={{maxWidth: 400, fontFamily: 'monospace', fontSize: 0, color: 'fg.default'}}
                  inline={true}
                >
                  {payload.attestation.runnerEnvironment}
                </Truncate>
              ),
            },
            {
              propertyName: 'Source Repository Digest',
              propertyValue: (
                <Truncate
                  title={payload.attestation.sourceRepositoryDigest}
                  sx={{maxWidth: 400, fontFamily: 'monospace', fontSize: 0, color: 'fg.default'}}
                  inline={true}
                >
                  {payload.attestation.sourceRepositoryDigest}
                </Truncate>
              ),
            },
            {
              propertyName: 'Source Repository Identifier',
              propertyValue: (
                <Truncate
                  title={payload.attestation.sourceRepositoryIdentifier}
                  sx={{maxWidth: 400, fontFamily: 'monospace', fontSize: 0, color: 'fg.default'}}
                  inline={true}
                >
                  {payload.attestation.sourceRepositoryIdentifier}
                </Truncate>
              ),
            },
            {
              propertyName: 'Source Repository Owner Identifier',
              propertyValue: (
                <Truncate
                  title={payload.attestation.sourceRepositoryOwnerIdentifier}
                  sx={{maxWidth: 400, fontFamily: 'monospace', fontSize: 0, color: 'fg.default'}}
                  inline={true}
                >
                  {payload.attestation.sourceRepositoryOwnerIdentifier}
                </Truncate>
              ),
            },
            {
              propertyName: 'Source Repository Owner URI',
              propertyValue: (
                <Truncate
                  title={payload.attestation.sourceRepositoryOwnerUri}
                  sx={{maxWidth: 400, fontFamily: 'monospace', fontSize: 0, color: 'fg.default'}}
                  inline={true}
                >
                  {payload.attestation.sourceRepositoryOwnerUri}
                </Truncate>
              ),
            },
            {
              propertyName: 'Source Repository Ref',
              propertyValue: (
                <Truncate
                  title={payload.attestation.sourceRepositoryRef}
                  sx={{maxWidth: 400, fontFamily: 'monospace', fontSize: 0, color: 'fg.default'}}
                  inline={true}
                >
                  {payload.attestation.sourceRepositoryRef}
                </Truncate>
              ),
            },
            {
              propertyName: 'Source Repository URI',
              propertyValue: (
                <Truncate
                  title={payload.attestation.sourceRepositoryUri}
                  sx={{maxWidth: 400, fontFamily: 'monospace', fontSize: 0, color: 'fg.default'}}
                  inline={true}
                >
                  {payload.attestation.sourceRepositoryUri}
                </Truncate>
              ),
            },
            {
              propertyName: 'Source Repository Visibility',
              propertyValue: (
                <Truncate
                  title={payload.attestation.sourceRepositoryVisibilityAtSigning}
                  sx={{maxWidth: 400, fontFamily: 'monospace', fontSize: 0, color: 'fg.default'}}
                  inline={true}
                >
                  {payload.attestation.sourceRepositoryVisibilityAtSigning}
                </Truncate>
              ),
            },
          ]}
        />
        {lines.length > 0 && (
          <Box sx={{m: 0, mt: 4, overflowX: 'auto'}}>
            <StaticFileFrame
              header={
                <Text sx={{color: 'fg.default', fontWeight: 'bold', fontSize: 1, lineHeight: 1, pl: 2}}>
                  Attestation
                </Text>
              }
            >
              <StaticSnippet lines={lines} />
            </StaticFileFrame>
          </Box>
        )}
      </PageLayout.Content>
    </PageLayout>
  )
}

try{ AttestationDetail.displayName ||= 'AttestationDetail' } catch {}